'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { volunteerApplicationService } from '@/lib/services/volunteerApplicationService';

interface VolunteerOpportunity {
  id: number;
  title: string;
  location: string;
  timeCommitment: string;
  skills: string[];
  description: string;
  image: string;
  alt: string;
}

const VolunteerSection: React.FC = () => {
  const [selectedOpportunity, setSelectedOpportunity] = useState<VolunteerOpportunity | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    motivation: '',
    availability: ''
  });

  const volunteerOpportunities: VolunteerOpportunity[] = [
  {
    id: 1,
    title: 'Social Media Ambassador',
    location: 'Remote',
    timeCommitment: '5-10 hours/week',
    skills: ['Social Media', 'Content Creation', 'Graphic Design'],
    description: 'Create engaging content and manage social media presence to spread SIG Day message across digital platforms.',
    image: 'https://images.unsplash.com/photo-1676275773814-ad0819d45436',
    alt: 'Person working on laptop with social media icons displayed on screen in modern office'
  },
  {
    id: 2,
    title: 'Event Coordinator',
    location: 'New York, USA',
    timeCommitment: '10-15 hours/week',
    skills: ['Event Planning', 'Project Management', 'Communication'],
    description: 'Plan and execute local SIG Day celebrations and community gatherings to bring people together in peace.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b9c5f58a-1767536392081.png',
    alt: 'Group of diverse people collaborating around table with event planning materials'
  },
  {
    id: 3,
    title: 'Translation Volunteer',
    location: 'Remote',
    timeCommitment: '3-5 hours/week',
    skills: ['Translation', 'Writing', 'Cultural Sensitivity'],
    description: 'Translate peace-building resources and materials to make the movement accessible to global communities.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d4f32171-1766911826734.png',
    alt: 'Multilingual books and translation documents spread across wooden desk'
  },
  {
    id: 4,
    title: 'Community Outreach Specialist',
    location: 'London, UK',
    timeCommitment: '8-12 hours/week',
    skills: ['Public Speaking', 'Networking', 'Community Organizing'],
    description: 'Build relationships with local organizations and faith communities to expand the movement\'s reach.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b9a8d80a-1767424368660.png',
    alt: 'Community leader speaking to engaged audience at outdoor gathering'
  }];


  const handleApply = (opportunity: VolunteerOpportunity) => {
    console.log('Volunteer opportunity selected:', opportunity.title);
    setSelectedOpportunity(opportunity);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Volunteer form submitted');
    if (!selectedOpportunity) return;

    setLoading(true);
    try {
      const result = await volunteerApplicationService.apply({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        opportunityId: selectedOpportunity.id,
        opportunityTitle: selectedOpportunity.title,
        location: selectedOpportunity.location,
        skills: selectedOpportunity.skills,
        motivation: formData.motivation,
        availability: formData.availability
      });

      if (result) {
        alert(`Volunteer Application Submitted!\n\nThank you, ${formData.fullName}!\n\nYour application for ${selectedOpportunity.title} has been received.\n\nOur volunteer coordinator will review your application and contact you at ${formData.email} within 3-5 business days.\n\nThank you for your willingness to serve the Global Peace Movement!`);
        setShowForm(false);
        setFormData({ fullName: '', email: '', phone: '', motivation: '', availability: '' });
        setSelectedOpportunity(null);
      }
    } catch (error) {
      alert('Application submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Volunteer Opportunities
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Use your skills to advance the movement. Find opportunities matched to your location and interests.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {volunteerOpportunities.map((opportunity) =>
          <div
            key={opportunity.id}
            className="bg-card rounded-lg overflow-hidden border border-border hover:shadow-lg transition-all duration-300">
            
              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={opportunity.image}
                alt={opportunity.alt}
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
              
              </div>

              <div className="p-6">
                <h3 className="text-xl font-headline font-bold text-foreground mb-3">{opportunity.title}</h3>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="MapPinIcon" size={16} className="text-primary" />
                    <span>{opportunity.location}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="ClockIcon" size={16} className="text-primary" />
                    <span>{opportunity.timeCommitment}</span>
                  </div>
                </div>

                <p className="text-sm text-foreground mb-4 line-clamp-3">{opportunity.description}</p>

                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2">Required Skills:</h4>
                  <div className="flex flex-wrap gap-2">
                    {opportunity.skills.map((skill, index) =>
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full">
                    
                        {skill}
                      </span>
                  )}
                  </div>
                </div>

                <button
                  onClick={() => handleApply(opportunity)}
                  className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-all duration-300 hover:bg-primary/90 hover:shadow-md"
                >
                  Apply Now
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showForm && selectedOpportunity &&
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-card rounded-lg max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button
            onClick={() => setShowForm(false)}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
            aria-label="Close form">
            
              <Icon name="XMarkIcon" size={24} />
            </button>

            <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
              Apply for {selectedOpportunity.title}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {selectedOpportunity.location} • {selectedOpportunity.timeCommitment}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <input
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter your full name" />
              
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email *
                </label>
                <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="your.email@example.com" />
              
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone (Optional)
                </label>
                <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+1 (555) 123-4567" />
              
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Why do you want to volunteer? *
                </label>
                <textarea
                required
                value={formData.motivation}
                onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Share your motivation..." />
              
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Your Availability *
                </label>
                <input
                type="text"
                required
                value={formData.availability}
                onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., Weekends, 10-15 hours/week" />
              
              </div>

              <div className="flex space-x-3">
                <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-2 border border-border rounded-md text-foreground hover:bg-muted transition-colors">
                
                  Cancel
                </button>
                <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md font-bold hover:bg-primary/90 transition-colors disabled:opacity-50">
                
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    </section>);

};

export default VolunteerSection;